import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { PageHero, Pagination } from "../components/common";
import ContentCardSkeleton from "../components/common/ContentCardSkeleton";
import { InitiativeCard } from "../components/initiatives";
import { BlogPost, ResearchPost, Initiative } from "../types";
import { useBlogPosts } from '../hooks/useBlogPosts';
import { useInitiativePosts } from '../hooks/useInitiativePosts';
import { useResearchPosts } from '../hooks/useResearchPosts';

const TagPage: React.FC = () => {
  const { tag } = useParams<{ tag: string }>();
  const { posts: apiBlogPosts, loading: blogLoading } = useBlogPosts(1, 100);
  const { posts: apiResearchPosts, loading: researchLoading } = useResearchPosts(1, 100);
  const { initiatives: apiInitiatives, loading: initiativeLoading } = useInitiativePosts();
  const [currentPage, setCurrentPage] = useState(1);

  const isDataLoading = blogLoading || researchLoading || initiativeLoading;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Filter API data by tag
  const decodedTag = tag ? decodeURIComponent(tag) : '';

  // Filter API blog posts by tag
  const filteredBlogPosts = apiBlogPosts.filter((post: any) => {
    const tags = Array.isArray(post.eng_tags)
      ? post.eng_tags
          .map((tag: any) => tag?.tag_name_eng)
          .filter((tagName: any): tagName is string => Boolean(tagName))
      : [];
    return tags.some((blogTag: any) => 
      blogTag.toLowerCase() === decodedTag.toLowerCase()
    );
  });

  // Format blog posts for display
  const formattedBlogPosts: BlogPost[] = filteredBlogPosts.map((post: any) => ({
    slug: post.id.toString(),
    image: post.eng_thumbnail || '/images/blog/blog-1.png',
    imageAlt: post.eng_title || 'Blog Post',
    title: post.eng_title || "Blog",
    excerpt: post.eng_caption || post.eng_description || '',
    date:
      // Prefer created_at when available, then created_date, then published_at
      post.created_at || post.created_date || post.published_at || '',
    readTime: '5 min read',
    tags: Array.isArray(post.eng_tags)
      ? post.eng_tags
          .map((tag: any) => tag?.tag_name_eng)
          .filter((tagName: any): tagName is string => Boolean(tagName))
      : [],
    link: `/blog/${post.id}`,
    detailsLink: `/blog/${post.id}`,
    downloadLink: "#",
  }));

  // Filter API research posts by tag
  const filteredResearchPosts = apiResearchPosts.filter((post: any) => {
    const tags = Array.isArray(post.eng_tags)
      ? post.eng_tags
          .map((tag: any) => tag?.tag_name_eng)
          .filter((tagName: any): tagName is string => Boolean(tagName))
      : [];
    return tags.some((researchTag: any) => 
      researchTag.toLowerCase() === decodedTag.toLowerCase()
    );
  });

  // Format research posts for display
  const formattedResearchPosts: ResearchPost[] = filteredResearchPosts.map((post: any) => ({
    slug: post.id.toString(),
    image: post.eng_thumbnail || '/images/research/research1.png',
    imageAlt: post.eng_title || 'Research Post',
    title: post.eng_title || "Research",
    excerpt: post.eng_caption || post.eng_description || '',
    date:
      // Prefer created_at when available, then created_date, then published_at
      post.created_at || post.created_date || post.published_at || '',
    readTime: '5 min read',
    tags: Array.isArray(post.eng_tags)
      ? post.eng_tags
          .map((tag: any) => tag?.tag_name_eng)
          .filter((tagName: any): tagName is string => Boolean(tagName))
      : [],
    detailsLink: `/research/${post.id}`,
    downloadLink: '#',
  }));

  // Filter API initiatives by tag
  const initiativeFiltered = apiInitiatives.filter((initiative: any) => {
    const tags = Array.isArray(initiative.eng_tags)
      ? initiative.eng_tags
          .map((tag: any) => tag?.tag_name_eng)
          .filter((tagName: any): tagName is string => Boolean(tagName))
      : [];
    return tags.some((initiativeTag: any) => 
      initiativeTag.toLowerCase() === decodedTag.toLowerCase()
    );
  });

  // Convert API initiatives to Initiative format for display
  const formattedInitiatives: Initiative[] = initiativeFiltered.map((initiative: any) => ({
    id: initiative.id,
    slug: initiative.id.toString(),
    tagline: '',
    title: initiative.eng_title,
    description: initiative.eng_caption || initiative.eng_description || '',
    image: initiative.eng_thumbnail || '/images/initiatives/Picture1.png',
    imageAlt: initiative.eng_title,
    features: [],
    buttons: [
      {
        text: 'Know More',
        href: `/initiatives/${initiative.id}`,
        variant: 'line'
      }
    ],
    status: 'ongoing' as const,
    tags: Array.isArray(initiative.eng_tags)
      ? initiative.eng_tags
          .map((tag: any) => tag?.tag_name_eng)
          .filter((tagName: any): tagName is string => Boolean(tagName))
      : []
  }));

  // Combine all filtered content for pagination
  const allContent = [...formattedBlogPosts, ...formattedResearchPosts, ...formattedInitiatives];
  
  // Get current page content
  const totalPages = Math.max(1, Math.ceil(allContent.length / 6));
  
  // Get current page content
  const startIndex = (currentPage - 1) * 6;
  const endIndex = startIndex + 6;
  const currentContent = allContent.slice(startIndex, endIndex);

  return (
    <main id="main-content" role="main" className="page-research">
      <PageHero
        title={`Results for "${decodedTag}"`}
        subtitle={`Discover all content filtered by ${decodedTag} tag`}
        className="blog-hero"
      />

      <section
        className="blog-page-section"
        style={{ padding: "80px 0 100px 0", background: "white" }}
      >
        <div className="container">
          {!decodedTag ? (
            <div className="text-center py-5">
              <p>No tag specified.</p>
            </div>
          ) : isDataLoading ? (
            <div className="row" aria-busy="true" aria-label="Loading content">
              <ContentCardSkeleton variant="card" />
              <ContentCardSkeleton variant="card" />
              <ContentCardSkeleton variant="card" />
              <ContentCardSkeleton variant="initiative" />
              <ContentCardSkeleton variant="initiative" />
              <ContentCardSkeleton variant="card" />
            </div>
          ) : (
            <>
              {currentContent.length > 0 ? (
                <div className="row">
                  {currentContent.map((item, index) => {
                    // Determine content type and render appropriate card
                    if ('slug' in item && 'link' in item && !('detailsLink' in item)) {
                      // Blog Post - Use inline blog-card structure with non-clickable tags
                      const blogPost = item as BlogPost;
                      return (
                        <div key={`blog-${index}`} className="col-xl-4 col-lg-4 col-md-6 mb-4">
                          <div className="blog-card">
                            <div className="blog-card__image">
                              <img src={blogPost.image} alt={blogPost.imageAlt} />
                              <div className="blog-card__overlay">
                                <Link to={blogPost.link} className="blog-card__link">
                                  <i className="fas fa-arrow-right"></i>
                                </Link>
                              </div>
                            </div>
                            <div className="blog-card__content">
                              <h3 className="blog-card__title">
                                <Link to={blogPost.link}>{blogPost.title}</Link>
                              </h3>
                              <p className="blog-card__excerpt">{blogPost.excerpt}</p>
                              {blogPost.tags && blogPost.tags.length > 0 && (
                                <div className="blog-card__tags mb-2">
                                  {blogPost.tags.map((tag, tagIndex) => (
                                    <span key={tagIndex} className="tag">
                                      {tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    } else if ('detailsLink' in item) {
                      // Research Post - Use inline blog-card structure with clickable tags
                      const researchPost = item as ResearchPost;
                      return (
                        <div key={`research-${index}`} className="col-xl-4 col-lg-4 col-md-6 mb-4">
                          <div className="blog-card">
                            <div className="blog-card__image">
                              <img src={researchPost.image} alt={researchPost.imageAlt} />
                              <div className="blog-card__overlay">
                                <Link to={researchPost.detailsLink} className="blog-card__link">
                                  <i className="fas fa-arrow-right"></i>
                                </Link>
                              </div>
                            </div>
                            <div className="blog-card__content">
                              <h3 className="blog-card__title">
                                <Link to={researchPost.detailsLink}>{researchPost.title}</Link>
                              </h3>
                              <p className="blog-card__excerpt">{researchPost.excerpt}</p>
                              {researchPost.tags && researchPost.tags.length > 0 && (
                                <div className="blog-card__tags mb-2">
                                  {researchPost.tags.map((tag, tagIndex) => (
                                    <Link 
                                      key={tagIndex} 
                                      className="tag" 
                                      to={researchPost.detailsLink}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ textDecoration: 'none' }}
                                    >
                                      {tag}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    } else {
                      // Initiative - Use InitiativeCard component like in Initiatives.tsx
                      const initiative = item as Initiative;
                      // Filter out "initiative" tag from tags
                      const filteredTags = initiative.tags ? initiative.tags.filter(tag => tag.toLowerCase() !== 'initiative') : [];
                      return (
                        <div key={`initiative-${index}`} className="col-lg-6 mb-4">
                          <InitiativeCard
                            image={initiative.image}
                            imageAlt={initiative.imageAlt}
                            title={initiative.title}
                            description={initiative.description}
                            buttons={
                              initiative.buttons && initiative.buttons.length > 0
                                ? initiative.buttons
                                : [
                                    {
                                      text: "Know More",
                                      href: `/initiatives/${initiative.slug || initiative.title}`,
                                      variant: "line" as const,
                                    },
                                  ]
                            }
                            tags={filteredTags}
                          />
                        </div>
                      );
                    }
                  })}
                </div>
              ) : (
                <div className="text-center py-5">
                  <h3>No content found</h3>
                  <p className="mb-4">
                    No content found with the tag "{decodedTag}".
                  </p>
                  <Link to="/blog" className="btn-main">
                    Browse All Content
                  </Link>
                </div>
              )}

              {!isDataLoading && totalPages > 1 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default TagPage;
